import React from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Button,
  CircularProgress,
  Box,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import analytics from '../../utils/analytics';

export interface HeroDialogProps {
  open: boolean;
  onClose: () => void;
  heroImage: string;
  heroFadeColor?: string;
  logo?: React.ReactNode;
  title?: React.ReactNode;
  body?: React.ReactNode;
  buttonText?: string;
  loading?: boolean;
  onConfirm?: () => void;
  footer?: React.ReactNode;
  pageCall?: {
    category: string;
    name: string;
    props?: Record<string, string>;
  };
}

export default function HeroDialog({
  open,
  onClose,
  heroImage,
  heroFadeColor = '#ffffff',
  logo,
  title,
  body,
  buttonText,
  loading = false,
  onConfirm,
  footer,
  pageCall,
}: HeroDialogProps) {
  const theme = useTheme();
  React.useEffect(() => {
    if (open && pageCall) {
      analytics.page(pageCall.category, pageCall.name, pageCall.props);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: 416,
          borderRadius: { xs: '28px 28px 0 0', sm: '28px' },
          overflow: 'hidden',
          m: { xs: 0, sm: 2 },
          alignSelf: { xs: 'flex-end', sm: 'center' },
          bgcolor: heroFadeColor,
          boxShadow: '0 30px 70px rgba(26,20,61,.24)',
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(26,20,61,.55)',
        },
      }}
    >
      <Box sx={{
        position: 'relative', height: 288, flexShrink: 0, bgcolor: heroFadeColor,
      }}
      >
        <Box
          component="img"
          src={heroImage}
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            maskImage: 'linear-gradient(to bottom, #000 0%, #000 46%, transparent 92%)',
            WebkitMaskImage: 'linear-gradient(to bottom, #000 0%, #000 46%, transparent 92%)',
          }}
        />
        <IconButton
          aria-label="Cerrar"
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            top: 14,
            right: 14,
            zIndex: 3,
            width: 34,
            height: 34,
            bgcolor: `${theme.palette.grey[200]}70`,
            '&:hover': { bgcolor: `${theme.palette.grey[100]}90` },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          position: 'relative',
          zIndex: 2,
          mt: '-78px',
          pt: 0,
          px: '26px',
          pb: '28px',
          bgcolor: heroFadeColor,
          '&.MuiDialogContent-root': { pt: 0 },
        }}
      >
        {logo}
        {title}
        {body}

        {(onConfirm || buttonText) && (
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={loading}
            color="primary"
            fullWidth
            sx={{
              fontWeight: 600,
              fontSize: 15,
              letterSpacing: '-0.1px',
              borderRadius: '28px',
              minHeight: 48,
              textTransform: 'none',
              width: '100%',
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : buttonText}
          </Button>
        )}

        {footer}
      </DialogContent>
    </Dialog>
  );
}

HeroDialog.defaultProps = {
  heroFadeColor: '#ffffff',
  logo: undefined,
  title: undefined,
  body: undefined,
  buttonText: undefined,
  loading: false,
  onConfirm: undefined,
  footer: undefined,
  pageCall: undefined,
};
